import type Fuse from "fuse.js";
import type { IFuseOptions } from "fuse.js";
import {
  assistantSessionsPath,
  buildSessionsScopeParam,
} from "@/lib/assistant-sessions";
import type { AxiosInstance } from "axios";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { toast } from "sonner";
import { useApi } from "./useApi";
import { useOrgFromPathname } from "./useOrgFromPathname";
import {
  orgInstanceSlug,
  useConnectedGithubOrgsNonSuspense,
} from "./useConnectedGithubOrgs";
import type {
  CachedAssistantSession,
  SearchedSession,
} from "./useAssistantSessionCache";

const MIN_QUERY_LENGTH = 2;
const MAX_SEARCH_RESULTS = 8;
const SEARCH_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
const PAGE_SIZE = 500;
const MAX_SESSIONS_PER_POOL = 2000;
// Relevance runs from 0.7 (threshold) to 1 (exact); a full window of recency
// is worth 0.2, about one typo in a five-letter word, so a clean match from
// last week beats a one-typo match from today.
const RECENCY_WEIGHT = 0.2;
const FUSE_OPTIONS = {
  keys: ["title"],
  threshold: 0.3,
  ignoreLocation: true,
  ignoreFieldNorm: true,
  includeScore: true,
} satisfies IFuseOptions<CachedAssistantSession>;

// Mirrors sessionUserActivityAt in components/codesmith/utils, inlined because
// that module pulls zod into the shared bundle that hosts the palette (which is
// also why fuse.js loads lazily).
function userActivityMs(session: CachedAssistantSession): number {
  return new Date(session.last_user_message_at || session.created_at).getTime();
}

// Pages arrive pinned rows first, then by last user activity, so the window
// ends at the first page whose last unpinned row predates it.
async function fetchSessionsActiveSince(
  api: AxiosInstance,
  url: string,
  cutoffMs: number,
): Promise<CachedAssistantSession[]> {
  const sessions: CachedAssistantSession[] = [];
  let offset = 0;
  while (sessions.length < MAX_SESSIONS_PER_POOL) {
    const response = await api.get(`${url}&offset=${offset}`);
    const page = (response.data.sessions ?? []) as CachedAssistantSession[];
    sessions.push(...page);
    const last = page.at(-1);
    const pastWindow =
      !!last && !last.pinned && userActivityMs(last) < cutoffMs;
    if (page.length < PAGE_SIZE || pastWindow) break;
    offset = response.data.next_offset;
  }
  return sessions.filter((session) => userActivityMs(session) >= cutoffMs);
}

/**
 * Fuzzy title search over the sessions the user was active in during the
 * last two weeks, ranked by match quality plus a recency boost. The window
 * loads when the palette opens, so typing never waits on the network.
 */
export function useAssistantSessionSearch(
  searchTerm: string,
  userId?: number,
  enabled = true,
) {
  const api = useApi();
  const activeGithubOrg = useOrgFromPathname();
  const { connectedGithubOrgs } = useConnectedGithubOrgsNonSuspense();
  const codesmithEnabled =
    connectedGithubOrgs?.installations.find(
      (installation) => installation?.account?.login === activeGithubOrg,
    )?.codesmith_enabled ?? false;
  const instanceSlug = orgInstanceSlug(
    connectedGithubOrgs,
    activeGithubOrg ?? null,
  );
  const searchable = !!activeGithubOrg && !!instanceSlug && codesmithEnabled;

  const {
    data: fuse,
    dataUpdatedAt: indexedAtMs,
    isLoading,
  } = useQuery<Fuse<CachedAssistantSession>>(
    ["assistantSessionSearchIndex", instanceSlug, activeGithubOrg],
    async () => {
      const org = { orgName: activeGithubOrg!, instanceSlug: instanceSlug! };
      const url = `${assistantSessionsPath(
        org,
      )}?status=active&limit=${PAGE_SIZE}${buildSessionsScopeParam(null, org)}`;
      const cutoffMs = Date.now() - SEARCH_WINDOW_MS;
      // The user's own pool is fetched separately so an admin's busy team
      // can't push their sessions past the per-pool cap.
      const [pools, { default: FuseIndex }] = await Promise.all([
        Promise.all([
          fetchSessionsActiveSince(api, `${url}&ownership=mine`, cutoffMs),
          fetchSessionsActiveSince(api, url, cutoffMs),
        ]),
        import("fuse.js"),
      ]);
      // Offset pages over a moving activity order can repeat a row.
      const byId = new Map(
        pools.flat().map((session) => [session.id, session]),
      );
      return new FuseIndex([...byId.values()], FUSE_OPTIONS);
    },
    {
      enabled: enabled && searchable,
      // Refetch on every palette open so new, renamed and archived sessions
      // show up; the cached window still answers while it loads.
      staleTime: 0,
      refetchOnWindowFocus: false,
      onError: () =>
        toast.error("Failed to search sessions", {
          id: "assistant-session-search",
        }),
    },
  );

  const trimmed = searchTerm.trim();
  const queryActive = searchable && trimmed.length >= MIN_QUERY_LENGTH;

  const sessions = useMemo<SearchedSession[]>(() => {
    if (!queryActive || !fuse) return [];
    const terms = trimmed.split(/\s+/);
    // Scored per term rather than with Fuse's $and, which multiplies term
    // scores so one exact term hides a sloppy one. A session's relevance is
    // its worst term.
    const [firstTermMatches, ...otherTermMatches] = terms.map((term) =>
      fuse.search(term),
    );
    const otherTermScores = otherTermMatches.map(
      (matches) =>
        new Map(matches.map(({ item, score = 1 }) => [item.id, score])),
    );
    const scored = firstTermMatches.flatMap(({ item, score = 1 }) => {
      let worstScore = score;
      for (const scores of otherTermScores) {
        const termScore = scores.get(item.id);
        if (termScore === undefined) return [];
        worstScore = Math.max(worstScore, termScore);
      }
      const ageMs = Math.max(0, indexedAtMs - userActivityMs(item));
      const recency = Math.max(0, 1 - ageMs / SEARCH_WINDOW_MS);
      return [{ item, rank: 1 - worstScore + RECENCY_WEIGHT * recency }];
    });
    const ranked = scored
      .sort((a, b) => b.rank - a.rank)
      .map(({ item }) => ({
        ...item,
        isMine: userId != null && item.user_id === userId,
      }));
    const mine = ranked.filter((session) => session.isMine);
    const team = ranked.filter((session) => !session.isMine);
    return [...mine, ...team].slice(0, MAX_SEARCH_RESULTS);
  }, [fuse, indexedAtMs, queryActive, trimmed, userId]);

  return {
    sessions,
    isLoading: enabled && queryActive && isLoading,
  };
}
